/**
 * The new-flow form, without the fetching — split from `NewFlowPage` so
 * `verify-flow-screens` can render it without reaching `lib/config.ts` (which
 * reads `import.meta.env` and only evaluates under Vite). See `NewFlowPage`
 * for what the form is for.
 */
import { FLOW_TEMPLATES, FLOW_TEMPLATE_ID_VALUES, type FlowTemplateId, type SegmentSummary } from '@wend/shared';
import { Button, FormField, Input, cn, focusRing } from '@wend/ui';
import { useState, type FormEvent, type KeyboardEvent } from 'react';

import { FormAlert } from '../../components/FormAlert';

export interface NewFlowFormProps {
  segments: readonly SegmentSummary[];
  initialTemplate: FlowTemplateId;
  initialSegment: string | null;
  submitting: boolean;
  error: string | null;
  onCreate: (input: { name: string; template: FlowTemplateId; audienceSegmentId: string | null }) => void;
}

/** The form, without the fetching — rendered by `verify-flow-screens`. */
export function NewFlowForm({ segments, initialTemplate, initialSegment, submitting, error, onCreate }: NewFlowFormProps) {
  const [template, setTemplate] = useState<FlowTemplateId>(initialTemplate);
  const [name, setName] = useState(FLOW_TEMPLATES[initialTemplate].label);
  const [touchedName, setTouchedName] = useState(false);
  const [segment, setSegment] = useState<string | null>(initialSegment);

  const pick = (id: FlowTemplateId) => {
    setTemplate(id);
    if (!touchedName) setName(FLOW_TEMPLATES[id].label);
  };

  const onRadioKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const ids = FLOW_TEMPLATE_ID_VALUES;
    const at = ids.indexOf(template);
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? ids[(at + 1) % ids.length]
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? ids[(at - 1 + ids.length) % ids.length]
          : event.key === 'Home'
            ? ids[0]
            : event.key === 'End'
              ? ids[ids.length - 1]
              : undefined;
    if (next === undefined) return;
    event.preventDefault();
    pick(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-template="${next}"]`)?.focus();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') return;
    onCreate({ name: trimmed, template, audienceSegmentId: segment });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-role="new-flow-form">
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="mb-2 text-[0.95rem] font-semibold">Start from</legend>
        {/* A radio group behaves as one: a single Tab stop (roving tabindex) and
            the arrow keys, Home and End move the choice — what a screen reader
            announcing "radio, 1 of 4" promises (slice 71 review). */}
        <div role="radiogroup" aria-label="Start from" onKeyDown={onRadioKey} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FLOW_TEMPLATE_ID_VALUES.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={template === id}
              tabIndex={template === id ? 0 : -1}
              onClick={() => pick(id)}
              data-template={id}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-lg border px-4 py-3.5 text-left transition-colors',
                template === id ? 'border-brand-b bg-brand-a/12 shadow-selected' : 'border-line bg-white/[0.02] hover:border-white/25',
                focusRing,
              )}
            >
              <span className="text-[0.95rem] font-semibold text-text">{FLOW_TEMPLATES[id].label}</span>
              <span className="text-[0.84rem] leading-snug text-muted">{FLOW_TEMPLATES[id].description}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <FormField label="Name">
        <Input
          value={name}
          maxLength={120}
          onChange={(event) => {
            setTouchedName(true);
            setName(event.target.value);
          }}
        />
      </FormField>

      {/* A raw <select> does not read FormField's context, so the id is given
          to both: without it the label pointed at nothing and screen readers
          announced an unnamed combo box (slice 71 review). */}
      <FormField
        id="new-flow-segment"
        label="Audience segment"
        description={
          template === 'welcome_leads'
            ? 'Optional. Empty: every new Reach lead can enter.'
            : template === 'blank'
              ? 'Optional. You can choose a trigger and audience in the builder.'
              : 'Who this flow is for. A rule segment grows on its own; you can change it in the builder.'
        }
      >
        <select
          id="new-flow-segment"
          aria-describedby="new-flow-segment-description"
          className="w-full rounded-base border border-line-strong bg-white/[0.04] px-3.5 py-2.5 text-[0.95rem] text-text [color-scheme:dark] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b"
          value={segment ?? ''}
          onChange={(event) => setSegment(event.target.value === '' ? null : event.target.value)}
        >
          <option value="">No segment (optional)</option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.counts.uniqueRecipients.toLocaleString('en-IN')} people{s.type === 'rule' ? ' · Dynamic' : ''}
            </option>
          ))}
        </select>
      </FormField>

      {error && <FormAlert tone="error">{error}</FormAlert>}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" loading={submitting} disabled={name.trim() === ''}>
          Create draft
        </Button>
        <span className="text-[0.84rem] text-muted">Creates a draft and opens the builder. Nothing is sent or launched.</span>
      </div>
    </form>
  );
}
